/**
 * Live plan + checklist detail for one household (ops drawer / client views).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faChevronRight,
  faRefresh,
  faTimes,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { getFaClientDataGathering, recheckDgMonitoring } from "@/services/api/agentic-v2";
import type {
  ChecklistHydratedItem,
  ChecklistHydratedSection,
  ChecklistRating,
  FaClientChecklistPayload,
  FaClientDataGathering,
  PlanDataGatheringStatus,
} from "@/services/api/agentic-v2/types";
import { useOpenWindow } from "@/components/ui/os-layout/useWindow";
import { openArtifactById } from "@/components/artifacts/openArtifactDestination";
import { CHECKLIST_RATING_META, isOpenHardStop } from "@/components/artifacts/checklistUi";
import { Badge, type BadgeColor } from "@/components/ui/badge/Badge";
import { Button } from "@/components/ui/button/Button";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { LoadErrorPanel } from "@/components/ui/load-error-panel";
import { StatusDot } from "@/components/ui/status-dot";
import {
  WORKBENCH_BODY_TEXT_CLASS,
  WORKBENCH_CARD_META_EMPHASIS_CLASS,
  WORKBENCH_INSET_PANEL_CLASS,
  WORKBENCH_MUTED_TEXT_CLASS,
  WORKBENCH_SUBTLE_TEXT_CLASS,
  WORKBENCH_TOOLBAR_DIVIDER_CLASS,
} from "@/components/ui/workbench";
import clsx from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import { Accordion } from "@/components/ui/accordion";
import { clientsKeys } from "@/services/api/agentic-v2/queries/keys";
import {
  dgChecklistFingerprint,
  waitForUpdatedDgChecklist,
} from "@/services/api/agentic-v2/queries/waitForUpdatedDgChecklist";

const NEEDS_REGEN_LABEL = "Possibly needs regen";
const NEEDS_REGEN_TOOLTIP =
  "New documents arrived after the cheat sheet was built. They may not all be relevant — review before regenerating.";

const PLAN_STATUS_LABELS: Record<string, string> = {
  awaiting_docs: "Awaiting docs",
  needs_generation: "Needs cheat sheet",
  needs_regeneration: NEEDS_REGEN_LABEL,
  up_to_date: "Up to date",
  excluded: "Excluded",
  in_flight: "In flight",
};

const ACTIVE_STATUS_ORDER: Record<string, number> = {
  needs_regeneration: 0,
  needs_generation: 1,
  in_flight: 2,
  awaiting_docs: 3,
  up_to_date: 4,
};

const TABLE_LINK_CLASS =
  "text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400";

function formatPlanValue(value: number): string {
  if (!value) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(value);
}

function isExcludedPlan(plan: PlanDataGatheringStatus): boolean {
  return plan.excluded || plan.status === "excluded";
}

function partitionPlans(plans: PlanDataGatheringStatus[]) {
  const active: PlanDataGatheringStatus[] = [];
  const excluded: PlanDataGatheringStatus[] = [];
  for (const plan of plans) {
    if (isExcludedPlan(plan)) {
      excluded.push(plan);
    } else {
      active.push(plan);
    }
  }
  active.sort(
    (a, b) =>
      (ACTIVE_STATUS_ORDER[a.status] ?? 99) - (ACTIVE_STATUS_ORDER[b.status] ?? 99) ||
      b.value - a.value,
  );
  excluded.sort((a, b) => (a.provider ?? "").localeCompare(b.provider ?? ""));
  return { active, excluded };
}

function planStatusBadgeColor(status: string): BadgeColor {
  switch (status) {
    case "up_to_date":
      return "emerald";
    case "excluded":
      return "slateMuted";
    case "awaiting_docs":
      return "slate";
    default:
      return "amber";
  }
}

function PlanStatusBadge({
  status,
  exclusionReason,
}: {
  status: string;
  exclusionReason?: string | null;
}) {
  const label = PLAN_STATUS_LABELS[status] ?? status;
  const title =
    status === "excluded" && exclusionReason
      ? exclusionReason
      : status === "needs_regeneration"
        ? NEEDS_REGEN_TOOLTIP
        : undefined;
  return (
    <Badge color={planStatusBadgeColor(status)} size="sm" title={title}>
      {label}
    </Badge>
  );
}

function BoolIcon({ ok }: { ok: boolean }) {
  return (
    <FontAwesomeIcon
      icon={ok ? faCheck : faTimes}
      className={ok ? "text-emerald-500" : "text-slate-400 dark:text-slate-500"}
    />
  );
}

function PlanDetailRow({
  plan,
  taskActive,
  onViewCheatSheet,
  onOpenTask,
}: {
  plan: PlanDataGatheringStatus;
  taskActive: boolean;
  onViewCheatSheet: (artifactId: string, title: string) => void;
  onOpenTask?: (taskId: string) => void;
}) {
  const title = plan.plan_name?.trim() || plan.provider || "Plan";
  const taskId = plan.process_plan_task_id;
  return (
    <tr className="text-slate-700 dark:text-slate-200">
      <td className="px-4 py-2">
        <div className="font-medium">{plan.provider ?? "—"}</div>
        {plan.plan_name && (
          <div className={clsx("text-xs", WORKBENCH_MUTED_TEXT_CLASS)}>{plan.plan_name}</div>
        )}
        {plan.exclusion_reason && (
          <div className={clsx("mt-0.5 text-xs", WORKBENCH_SUBTLE_TEXT_CLASS)}>
            {plan.exclusion_reason}
          </div>
        )}
      </td>
      <td className={clsx("px-4 py-2 text-xs capitalize", WORKBENCH_MUTED_TEXT_CLASS)}>
        {plan.plan_type ?? "—"}
      </td>
      <td className="px-4 py-2 text-right tabular-nums">{formatPlanValue(plan.value)}</td>
      <td className="px-4 py-2 text-center">
        <BoolIcon ok={plan.has_docs} />
        {plan.doc_count > 0 && (
          <span className={clsx("ml-1 text-xs", WORKBENCH_SUBTLE_TEXT_CLASS)}>
            ({plan.doc_count})
          </span>
        )}
      </td>
      <td className="px-4 py-2 text-center">
        <BoolIcon ok={plan.has_cheat_sheet} />
      </td>
      <td className={clsx("px-4 py-2 text-xs", WORKBENCH_BODY_TEXT_CLASS)}>
        {plan.cheat_sheet_variant ?? "—"}
      </td>
      <td className="px-4 py-2">
        <PlanStatusBadge status={plan.status} exclusionReason={plan.exclusion_reason} />
      </td>
      <td className="px-4 py-2 text-right">
        <div className="flex flex-col items-end gap-1">
          {taskId && onOpenTask ? (
            <Button
              plain
              size="unstyled"
              className={clsx(TABLE_LINK_CLASS, taskActive && "font-semibold")}
              onClick={() => onOpenTask(taskId)}
            >
              Open task
            </Button>
          ) : null}
          {plan.cheat_sheet_artifact_id ? (
            <Button
              plain
              size="unstyled"
              className={TABLE_LINK_CLASS}
              onClick={() => onViewCheatSheet(plan.cheat_sheet_artifact_id!, title)}
            >
              View cheat sheet
            </Button>
          ) : !taskId ? (
            <span className={clsx("text-xs", WORKBENCH_SUBTLE_TEXT_CLASS)}>—</span>
          ) : null}
        </div>
      </td>
    </tr>
  );
}

function PlansTable({
  plans,
  activeTaskId,
  onViewCheatSheet,
  onOpenTask,
}: {
  plans: PlanDataGatheringStatus[];
  activeTaskId?: string | null;
  onViewCheatSheet: (artifactId: string, title: string) => void;
  onOpenTask?: (taskId: string) => void;
}) {
  if (!plans.length) return null;
  return (
    <table className="min-w-full text-sm">
      <thead className={clsx("text-left text-xs uppercase tracking-wide", WORKBENCH_MUTED_TEXT_CLASS)}>
        <tr>
          <th className="px-4 py-2 font-medium">Plan</th>
          <th className="px-4 py-2 font-medium">Type</th>
          <th className="px-4 py-2 font-medium text-right">Value</th>
          <th className="px-4 py-2 font-medium text-center">Docs</th>
          <th className="px-4 py-2 font-medium text-center">Cheat sheet</th>
          <th className="px-4 py-2 font-medium">Sheet type</th>
          <th className="px-4 py-2 font-medium">Status</th>
          <th className="px-4 py-2 font-medium text-right">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-white/5">
        {plans.map((plan) => (
          <PlanDetailRow
            key={plan.plan_uuid}
            plan={plan}
            taskActive={Boolean(
              plan.process_plan_task_id && plan.process_plan_task_id === activeTaskId,
            )}
            onViewCheatSheet={onViewCheatSheet}
            onOpenTask={onOpenTask}
          />
        ))}
      </tbody>
    </table>
  );
}

function PlanSummaryStrip({ summary }: { summary: FaClientDataGathering }) {
  const parts = [
    `${summary.plans_total} in scope`,
    summary.plans_up_to_date > 0 ? `${summary.plans_up_to_date} up to date` : null,
    summary.plans_needs_regeneration != null && summary.plans_needs_regeneration > 0
      ? `${summary.plans_needs_regeneration} possibly need regen`
      : null,
    summary.plans_awaiting_docs > 0 ? `${summary.plans_awaiting_docs} awaiting docs` : null,
    summary.plans_excluded > 0 ? `${summary.plans_excluded} excluded` : null,
  ].filter(Boolean);
  return (
    <p
      className={clsx(
        "px-4 py-2 text-xs",
        WORKBENCH_TOOLBAR_DIVIDER_CLASS,
        WORKBENCH_SUBTLE_TEXT_CLASS,
      )}
    >
      {parts.join(" · ")}
      {summary.value_covered_pct > 0 && (
        <span className="ml-2 tabular-nums font-medium text-slate-800 dark:text-slate-200">
          {summary.value_covered_pct}% value covered
        </span>
      )}
    </p>
  );
}

function hydrateChecklistSections(
  payload: FaClientChecklistPayload | null | undefined,
): ChecklistHydratedSection[] {
  if (!payload?.schema?.sections?.length) return [];
  const values = payload.data?.items ?? {};
  return payload.schema.sections.map((section) => ({
    id: section.id,
    title: section.title,
    items: (section.items ?? []).map((item) => {
      const value = values[item.id];
      const status = value?.status ?? "pending";
      return {
        ...item,
        status,
        evidence: value?.evidence,
        confirmed_by: value?.confirmed_by,
        confirmed_at: value?.confirmed_at,
      };
    }),
  }));
}

function checklistStatusMeta(item: ChecklistHydratedItem): {
  label: string;
  dot: "emerald" | "red" | "gray" | "amber";
  badge: BadgeColor;
} {
  switch (item.status) {
    case "complete":
      return { label: "Complete", dot: "emerald", badge: "emerald" };
    case "adequate":
      return { label: "Adequate", dot: "emerald", badge: "teal" };
    case "missing":
      return { label: "Missing", dot: "red", badge: "red" };
    case "not_required":
      return { label: "N/A", dot: "gray", badge: "slateMuted" };
    default:
      return { label: "Partial", dot: "amber", badge: "amber" };
  }
}

function sectionBlockingCount(section: ChecklistHydratedSection): number {
  return section.items.filter(isOpenHardStop).length;
}

/** Overall DG rating badge with the plain-language meaning as a tooltip. */
function ChecklistRatingBadge({ rating }: { rating: ChecklistRating }) {
  const meta = CHECKLIST_RATING_META[rating];
  return (
    <Badge
      color={meta.color}
      size="sm"
      title={meta.description}
      data-testid="dg-checklist-rating"
      className="!normal-case !tracking-normal !font-sans"
    >
      {meta.label}
    </Badge>
  );
}

function ChecklistItemStatus({ item }: { item: ChecklistHydratedItem }) {
  const { label, badge } = checklistStatusMeta(item);
  return (
    <Badge color={badge} size="sm" className="!normal-case !tracking-normal !font-sans shrink-0">
      {label}
    </Badge>
  );
}

function ChecklistSectionPanel({
  faClientId,
  summary,
  onUpdated,
  onOpenTask,
  darkSurface = false,
  sectionTitle = "Data gathering checklist",
  showProgressBar = true,
  showAgentSummary = true,
}: {
  faClientId: string;
  summary: FaClientDataGathering;
  onUpdated: (next: FaClientDataGathering) => void;
  onOpenTask?: (taskId: string) => void;
  darkSurface?: boolean;
  sectionTitle?: string;
  /** Client tab uses text stats + section badges instead of a second full-width bar. */
  showProgressBar?: boolean;
  showAgentSummary?: boolean;
}) {
  const queryClient = useQueryClient();
  const sections = hydrateChecklistSections(summary.checklist ?? null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshAbortRef = useRef<AbortController | null>(null);
  const canRefreshChecklist = summary.can_recheck_dg_monitoring === true;

  useEffect(
    () => () => {
      refreshAbortRef.current?.abort();
    },
    [],
  );

  const handleRefreshChecklist = async () => {
    if (!canRefreshChecklist || refreshing || !summary.dg_monitoring_task_id) return;
    refreshAbortRef.current?.abort();
    const abortController = new AbortController();
    refreshAbortRef.current = abortController;

    const baselineFingerprint = dgChecklistFingerprint(summary);
    const monitoringTaskId = summary.dg_monitoring_task_id;
    setRefreshing(true);
    onOpenTask?.(monitoringTaskId);
    try {
      await recheckDgMonitoring(faClientId);
      const updated = await waitForUpdatedDgChecklist(
        faClientId,
        baselineFingerprint,
        monitoringTaskId,
        { signal: abortController.signal },
      );
      if (updated) {
        queryClient.setQueriesData(
          { queryKey: [...clientsKeys.all, "data-gathering", faClientId] },
          () => updated,
        );
        onUpdated(updated);
      } else {
        await queryClient.invalidateQueries({
          queryKey: [...clientsKeys.all, "data-gathering", faClientId],
        });
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      throw err;
    } finally {
      if (refreshAbortRef.current === abortController) {
        refreshAbortRef.current = null;
      }
      setRefreshing(false);
    }
  };

  const refreshChecklistButton = canRefreshChecklist ? (
    <Button
      type="button"
      plain
      size="sm"
      onClick={() => void handleRefreshChecklist()}
      disabled={refreshing}
      loading={refreshing}
      className="h-7 gap-1.5 px-2 text-xs text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
    >
      <FontAwesomeIcon icon={faRefresh} />
      {refreshing ? "Refreshing…" : "Refresh checklist"}
    </Button>
  ) : null;

  if (!sections.length) {
    return (
      <div
        className={clsx(
          "border-t px-4 py-3 text-sm",
          WORKBENCH_TOOLBAR_DIVIDER_CLASS,
          WORKBENCH_MUTED_TEXT_CLASS,
        )}
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p>Checklist not started yet — the monitoring agent will populate this on its next cycle.</p>
          {refreshChecklistButton}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={clsx("mb-3 flex flex-wrap items-baseline justify-between gap-2", !showProgressBar && "mb-4")}>
        <h4
          id="dg-checklist-heading"
          className="text-[11px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          {sectionTitle}
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          {refreshChecklistButton}
          {summary.checklist_rating ? <ChecklistRatingBadge rating={summary.checklist_rating} /> : null}
          <p className={clsx("text-xs tabular-nums font-medium text-slate-700 dark:text-slate-200")}>
            {Math.round(summary.checklist_complete_pct)}% complete
          </p>
        </div>
      </div>

      {summary.checklist_blockers?.length ? (
        <p
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-200"
          data-testid="dg-checklist-blockers"
        >
          <span className="font-medium">Needed before narrative:</span> {summary.checklist_blockers.join(", ")}
        </p>
      ) : null}

      {showProgressBar ? (
        <div className="mb-4 h-1.5 overflow-hidden rounded-full bg-slate-200/80 dark:bg-white/10">
          <div
            className="h-full rounded-full bg-indigo-500 transition-all dark:bg-indigo-400"
            style={{ width: `${Math.min(100, summary.checklist_complete_pct)}%` }}
            role="progressbar"
            aria-valuenow={summary.checklist_complete_pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Checklist ${summary.checklist_complete_pct}% complete`}
          />
        </div>
      ) : null}

      {showAgentSummary && summary.checklist?.data?.overall_summary ? (
        <Accordion
          isExpanded={summaryOpen}
          onToggle={() => setSummaryOpen((open) => !open)}
          className="mb-4 border-white/10 !rounded-xl overflow-hidden dark:border-white/10"
          headerClassName="!bg-white/5 !px-3 !py-2.5 !hover:bg-white/[0.07] dark:!bg-white/5"
          contentClassName="!border-white/5 !px-3 !py-3 !text-xs !leading-relaxed text-slate-600 dark:!text-slate-300"
          header={
            <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
              Agent summary
            </span>
          }
        >
          {summary.checklist.data.overall_summary}
        </Accordion>
      ) : null}

      <div className="space-y-5">
        {sections.map((section) => {
          const blocking = sectionBlockingCount(section);
          const items = section.items;
          return (
            <div key={section.id}>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <p
                  className={clsx(
                    "text-xs font-semibold uppercase tracking-wide",
                    WORKBENCH_CARD_META_EMPHASIS_CLASS,
                  )}
                >
                  {section.title}
                </p>
                {blocking > 0 ? (
                  <Badge color="red" size="sm" className="!normal-case !tracking-normal !font-sans">
                    {blocking} blocked
                  </Badge>
                ) : null}
              </div>
              <ul
                className={clsx(
                  "divide-y overflow-hidden rounded-xl border",
                  darkSurface
                    ? "divide-white/5 border-white/10 bg-white/[0.03]"
                    : "divide-slate-100 border-slate-200 dark:divide-white/5 dark:border-white/10 dark:bg-white/[0.03]",
                )}
              >
                {items.map((item) => (
                  <li
                    key={item.id}
                    className="flex flex-wrap items-start justify-between gap-3 px-3 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <StatusDot
                          color={checklistStatusMeta(item).dot}
                          size="sm"
                        />
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                          {item.label}
                        </p>
                      </div>
                      {item.evidence && (
                        <p className={clsx("mt-1 pl-4 text-xs line-clamp-3", WORKBENCH_MUTED_TEXT_CLASS)}>
                          {item.evidence}
                        </p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {isOpenHardStop(item) ? (
                        <Badge
                          color="red"
                          size="sm"
                          title="Needed before narrative"
                          data-testid="dg-checklist-item-blocked"
                          className="!normal-case !tracking-normal !font-sans shrink-0"
                        >
                          Blocked
                        </Badge>
                      ) : null}
                      <ChecklistItemStatus item={item} />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function DataGatheringClientPlansPanel({
  faClientId,
  refreshKey = 0,
  onOpenTask,
  activeTaskId,
  surface = "default",
  variant = "full",
  summary: summaryProp,
  checklistSectionTitle = "Data gathering checklist",
  showChecklistProgressBar = true,
  showChecklistAgentSummary = true,
  wrapInCard = true,
}: {
  faClientId: string;
  refreshKey?: number;
  onOpenTask?: (taskId: string) => void;
  activeTaskId?: string | null;
  /** When nested in a dark workbench detail column (Guardians-style panel). */
  surface?: "default" | "workbench-dark";
  /** ``checklist`` skips the duplicate plans table (use client Plans rows instead). */
  variant?: "full" | "checklist";
  /** When provided, skips the internal fetch (e.g. parent already loaded DG summary). */
  summary?: FaClientDataGathering | null;
  checklistSectionTitle?: string;
  showChecklistProgressBar?: boolean;
  showChecklistAgentSummary?: boolean;
  wrapInCard?: boolean;
}) {
  const openWindow = useOpenWindow();
  const [summaryFetched, setSummaryFetched] = useState<FaClientDataGathering | null>(null);
  const [loading, setLoading] = useState(summaryProp === undefined);
  const [error, setError] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [excludedOpen, setExcludedOpen] = useState(false);
  const summary = summaryProp ?? summaryFetched;
  const checklistOnly = variant === "checklist";

  useEffect(() => {
    if (summaryProp !== undefined) {
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void getFaClientDataGathering(faClientId)
      .then((data) => {
        if (!cancelled) setSummaryFetched(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setSummaryFetched(null);
          setError(err instanceof Error ? err.message : "Failed to load plans");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [faClientId, refreshKey, fetchKey, summaryProp]);

  const { active, excluded } = useMemo(
    () => partitionPlans(summary?.plans ?? []),
    [summary?.plans],
  );

  const viewCheatSheet = (artifactId: string, title: string) => {
    void openArtifactById(artifactId, openWindow, { mode: "view", title });
  };

  if (loading) {
    return (
      <div className="p-4">
        <LoadingPlaceholder
          pattern={checklistOnly ? "list" : "table"}
          count={checklistOnly ? 3 : 4}
          text={checklistOnly ? "Loading checklist…" : "Loading plans…"}
        />
      </div>
    );
  }

  if (error) {
    return (
      <LoadErrorPanel
        error={error}
        onRetry={() => setFetchKey((key) => key + 1)}
        className="m-4"
      />
    );
  }

  if (!active.length && !excluded.length && !summary?.checklist_artifact_id && !checklistOnly) {
    return (
      <div className={clsx("px-4 py-4 text-sm", WORKBENCH_MUTED_TEXT_CLASS)}>No plans on file.</div>
    );
  }

  const darkSurface = surface === "workbench-dark";

  if (checklistOnly) {
    if (!summary) {
      return (
        <div className={clsx("px-1 py-2 text-sm", WORKBENCH_MUTED_TEXT_CLASS)}>
          Checklist not available yet.
        </div>
      );
    }
    const panel = (
      <ChecklistSectionPanel
        faClientId={faClientId}
        summary={summary}
        onUpdated={(next) => {
          if (summaryProp === undefined) setSummaryFetched(next);
        }}
        onOpenTask={onOpenTask}
        darkSurface={darkSurface}
        sectionTitle={checklistSectionTitle}
        showProgressBar={showChecklistProgressBar}
        showAgentSummary={showChecklistAgentSummary}
      />
    );

    if (!wrapInCard) return panel;

    return (
      <section
        className={clsx(
          "rounded-xl border border-white/10 bg-white/5 p-4 ring ring-white/5 ring-inset",
          darkSurface && "text-slate-100",
        )}
        aria-labelledby="dg-checklist-heading"
      >
        {panel}
      </section>
    );
  }

  return (
    <div className={clsx("min-h-0", darkSurface && "text-slate-100")}>
      {summary && (active.length > 0 || excluded.length > 0) && (
        <PlanSummaryStrip summary={summary} />
      )}
      {active.length > 0 && (
        <PlansTable
          plans={active}
          activeTaskId={activeTaskId}
          onViewCheatSheet={viewCheatSheet}
          onOpenTask={onOpenTask}
        />
      )}
      {excluded.length > 0 && (
        <div className={WORKBENCH_TOOLBAR_DIVIDER_CLASS}>
          <Button
            plain
            size="unstyled"
            className={clsx(
              "flex w-full items-center justify-between px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide",
              WORKBENCH_MUTED_TEXT_CLASS,
              "hover:bg-slate-100/80 dark:hover:bg-white/[0.04]",
            )}
            onClick={() => setExcludedOpen((open) => !open)}
          >
            <span>Excluded ({excluded.length})</span>
            <FontAwesomeIcon icon={excludedOpen ? faChevronDown : faChevronRight} />
          </Button>
          {excludedOpen && (
            <PlansTable
              plans={excluded}
              activeTaskId={activeTaskId}
              onViewCheatSheet={viewCheatSheet}
              onOpenTask={onOpenTask}
            />
          )}
        </div>
      )}
      {summary && (
        <ChecklistSectionPanel
          faClientId={faClientId}
          summary={summary}
          onUpdated={(next) => {
            if (summaryProp === undefined) setSummaryFetched(next);
          }}
          onOpenTask={onOpenTask}
          darkSurface={darkSurface}
          sectionTitle={checklistSectionTitle}
          showProgressBar={showChecklistProgressBar}
          showAgentSummary={showChecklistAgentSummary}
        />
      )}
    </div>
  );
}
